/**
 * Admin Dashboard - SaaS Organization Management
 * Accessible at: /admin-dashboard (requires superadmin authentication)
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, AlertTriangle, CheckCircle, XCircle, Trash2,
  RefreshCw, Search, Filter, Calendar, Mail, Globe, LogOut,
  BarChart3, FileText, Server, ExternalLink, Activity,
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import {
  ADMIN_API_URL, getStoredToken, removeStoredToken, authFetch, formatDate,
} from '../components/admin/adminAuth';
import LoginForm from '../components/admin/LoginForm';
import OrgStatusBadge from '../components/admin/OrgStatusBadge';
import OrganizationDetailPanel from '../components/admin/OrganizationDetailPanel';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!getStoredToken());
  const [authError, setAuthError] = useState(null);
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const handleLogin = (success, errorMsg) => {
    if (success) { setIsAuthenticated(true); setAuthError(null); }
    else { setAuthError(errorMsg || t("adminDashboard.errors.authFailed")); }
  };

  const handleLogout = () => {
    removeStoredToken();
    setIsAuthenticated(false);
    setStats(null);
    setOrganizations([]);
  };

  useEffect(() => {
    const verifyToken = async () => {
      const token = getStoredToken();
      if (!token) { setIsAuthenticated(false); return; }
      try {
        const response = await authFetch(`${ADMIN_API_URL}/v1/admin/auth/me`);
        if (!response.ok) { removeStoredToken(); setIsAuthenticated(false); }
      } catch { removeStoredToken(); setIsAuthenticated(false); }
    };
    verifyToken();
  }, []);

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/stats/dashboard`);
      if (!response.ok) throw new Error(t("adminDashboard.errors.fetchStats"));
      setStats(await response.json());
    } catch (err) { setError(err.message); }
  }, [isAuthenticated, t]);

  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations?${params}`);
      if (!response.ok) throw new Error(t("adminDashboard.errors.fetchOrganizations"));
      const data = await response.json();
      setOrganizations(data.organizations || []);
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [statusFilter, isAuthenticated, t]);

  useEffect(() => {
    if (isAuthenticated) { fetchStats(); fetchOrganizations(); }
  }, [isAuthenticated, fetchStats, fetchOrganizations]);

  const handleSuspend = async (e, orgId, subdomain) => {
    e.stopPropagation();
    if (!window.confirm(t("adminDashboard.confirmSuspend", { subdomain }))) return;
    setActionLoading(orgId);
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations/${orgId}/suspend`, { method: 'POST' });
      if (!response.ok) throw new Error(t("adminDashboard.errors.suspend"));
      await fetchOrganizations(); await fetchStats();
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const handleActivate = async (e, orgId, subdomain) => {
    e.stopPropagation();
    if (!window.confirm(t("adminDashboard.confirmActivate", { subdomain }))) return;
    setActionLoading(orgId);
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations/${orgId}/activate`, { method: 'POST' });
      if (!response.ok) throw new Error(t("adminDashboard.errors.activate"));
      await fetchOrganizations(); await fetchStats();
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (orgId, subdomain) => {
    setActionLoading(orgId);
    try {
      const response = await authFetch(
        `${ADMIN_API_URL}/v1/admin/organizations/${orgId}/delete-permanently?confirmation_code=${subdomain}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error(t("adminDashboard.errors.delete"));
      setShowDeleteConfirm(null);
      setSelectedOrg(null);
      await fetchOrganizations(); await fetchStats();
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const filteredOrgs = organizations.filter((org) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return org.subdomain?.toLowerCase().includes(term) || org.company_name?.toLowerCase().includes(term) || org.admin_email?.toLowerCase().includes(term);
  });

  if (!isAuthenticated) return <LoginForm onLogin={handleLogin} error={authError} />;

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">{t("adminDashboard.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("adminDashboard.subtitle")}</p>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors">
          <LogOut className="w-4 h-4" />{t("adminDashboard.logout")}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { label: t("adminDashboard.stats.total"), value: stats.total_organizations, color: 'blue', Icon: Building2 },
            { label: t("adminDashboard.stats.trial"), value: stats.trial_organizations, color: 'blue', Icon: Calendar },
            { label: t("adminDashboard.stats.active"), value: stats.active_organizations, color: 'green', Icon: CheckCircle },
            { label: t("adminDashboard.stats.suspended"), value: stats.suspended_organizations, color: 'red', Icon: XCircle },
          ].map(({ label, value, color, Icon }) => (
            <div key={label} className="bg-card rounded-xl shadow-sm p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{label}</p>
                  <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{value}</p>
                </div>
                <div className={`bg-${color}-100 p-3 rounded-lg`}><Icon className={`w-6 h-6 text-${color}-600`} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-sm p-4 mb-6 border border-border">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder={t("adminDashboard.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-muted-foreground w-5 h-5" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-border rounded-lg px-3 py-2 bg-background focus:ring-2 focus:ring-ring">
              <option value="">{t("adminDashboard.filters.allStatuses")}</option>
              <option value="trial">{t("adminDashboard.filters.trial")}</option>
              <option value="active">{t("adminDashboard.filters.active")}</option>
              <option value="suspended">{t("adminDashboard.filters.suspended")}</option>
              <option value="grace_period">{t("adminDashboard.filters.gracePeriod")}</option>
              <option value="cancelled">{t("adminDashboard.filters.cancelled")}</option>
            </select>
          </div>
          <button onClick={() => { fetchStats(); fetchOrganizations(); }} className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4" />{t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Organizations table */}
      <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
        {loading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-8 w-20 rounded" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500"><AlertTriangle className="w-8 h-8 mx-auto mb-2" />{error}</div>
        ) : filteredOrgs.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />{t("adminDashboard.noOrganizations")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  {[
                    t("adminDashboard.table.organization"),
                    t("adminDashboard.table.admin"),
                    t("adminDashboard.table.status"),
                    t("adminDashboard.table.createdAt"),
                    t("adminDashboard.table.trialEnd"),
                    t("adminDashboard.table.actions"),
                  ].map((h, i) => (
                    <th key={h} className={`px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredOrgs.map((org) => (
                  <tr key={org.id} onClick={() => setSelectedOrg(org)} className="hover:bg-accent/50 transition-colors cursor-pointer">
                    <td className="px-6 py-4">
                      <p className="font-medium">{org.company_name}</p>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground"><Globe className="w-3 h-3" /><span className="text-primary">{org.subdomain}.mawered.com</span></div>
                    </td>
                    <td className="px-6 py-4"><div className="flex items-center gap-1 text-sm text-muted-foreground"><Mail className="w-3 h-3" />{org.admin_email}</div></td>
                    <td className="px-6 py-4"><OrgStatusBadge status={org.status} small /></td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(org.created_at, true)}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(org.trial_end_date, true)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {actionLoading === org.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : (
                          <>
                            {org.status === 'suspended' ? (
                              <button onClick={(e) => handleActivate(e, org.id, org.subdomain)} className="p-2 text-green-600 hover:bg-accent rounded-lg transition-colors" title={t("adminDashboard.actions.activate")}>
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            ) : (
                              <button onClick={(e) => handleSuspend(e, org.id, org.subdomain)} className="p-2 text-orange-600 hover:bg-accent rounded-lg transition-colors" title={t("adminDashboard.actions.suspend")}>
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(org); }} className="p-2 text-red-600 hover:bg-accent rounded-lg transition-colors" title={t("adminDashboard.actions.delete")}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monitoring & Observabilité — accès ops interne Teknovat */}
      <div className="mt-8 bg-card rounded-xl shadow-sm border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Activity className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold">{t("miscPages.adminDash.monitoringTitle")}</h2>
              <p className="text-xs text-muted-foreground">{t("miscPages.adminDash.monitoringSub")}</p>
            </div>
          </div>
          <a
            href="/monitoring"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 hover:border-primary/40 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            {t("miscPages.adminDash.integratedView")}
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              label: 'Backend API',
              desc: t("miscPages.adminDash.backendDesc"),
              icon: BarChart3,
              color: 'blue',
              uid: 'mawered-backend',
            },
            {
              label: t("miscPages.adminDash.appLogs"),
              desc: t("miscPages.adminDash.appLogsDesc"),
              icon: FileText,
              color: 'orange',
              uid: 'mawered-logs',
            },
            {
              label: t("miscPages.adminDash.vpsSystem"),
              desc: t("miscPages.adminDash.vpsDesc"),
              icon: Server,
              color: 'green',
              uid: 'mawered-system',
            },
          ].map(({ label, desc, icon: Icon, color, uid }) => (
            <a
              key={uid}
              href={`/monitoring`}
              onClick={(e) => { e.preventDefault(); window.open(`/grafana/d/${uid}`, '_blank'); }}
              className="flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 transition-all cursor-pointer group"
            >
              <div className={`p-2 rounded-lg bg-${color}-500/10 flex-shrink-0`}>
                <Icon className={`w-4 h-4 text-${color}-400`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium group-hover:text-primary transition-colors">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
              <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
            </a>
          ))}
        </div>
      </div>

      {selectedOrg && (
        <OrganizationDetailPanel
          org={selectedOrg}
          onClose={() => setSelectedOrg(null)}
          onRefresh={() => { fetchStats(); fetchOrganizations(); }}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 max-w-md w-full mx-4" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-destructive/10 p-2 rounded-full"><AlertTriangle className="w-6 h-6 text-destructive" /></div>
              <h3 className="text-lg font-bold">{t("adminDashboard.deleteModal.title")}</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              {t("adminDashboard.deleteModal.confirmPre")} <strong>{showDeleteConfirm.company_name}</strong> ({showDeleteConfirm.subdomain}) ?
            </p>
            <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg mb-4">
              {t("adminDashboard.deleteModal.warning")}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors">{t("common.cancel")}</button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id, showDeleteConfirm.subdomain)}
                disabled={actionLoading === showDeleteConfirm.id}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                {actionLoading === showDeleteConfirm.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {t("adminDashboard.actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
